import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { hasError = false, leftAddon, rightAddon, className, type = 'text', ...rest },
  ref,
) {
  const input = (
    <input
      ref={ref}
      type={type}
      aria-invalid={hasError || undefined}
      className={cn(
        'h-10 w-full bg-bg text-foreground placeholder:text-muted',
        'border rounded-md px-3 text-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        hasError ? 'border-danger focus-visible:ring-danger' : 'border-border',
        leftAddon && 'rounded-l-none',
        rightAddon && 'rounded-r-none',
        className,
      )}
      {...rest}
    />
  );

  if (!leftAddon && !rightAddon) return input;

  return (
    <div className="flex w-full">
      {leftAddon && (
        <span className="inline-flex items-center rounded-l-md border border-r-0 border-border bg-surface px-3 text-sm text-muted">
          {leftAddon}
        </span>
      )}
      {input}
      {rightAddon && (
        <span className="inline-flex items-center rounded-r-md border border-l-0 border-border bg-surface px-3 text-sm text-muted">
          {rightAddon}
        </span>
      )}
    </div>
  );
});
